import { useMutation } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { useApi } from '@fortem-monorepo/react-spa'
import { Website } from '@fortem-monorepo/web-sdk'
import { API } from '@fortem-monorepo/web-sdk'

import { toastApiError } from '@/lib/error-toast'

const useInvalidatePosmon = () => {
  const client = useQueryClient()
  return () => {
    client.invalidateQueries({ queryKey: ['flow', 'posmon', 'summary'] })
  }
}

export const useUpdateProductDetailsMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (body: API.UpdateBody) => {
      return api.website.updateInvestmentSecurity(body)
    },
    onSuccess: (_data, _params) => {
      toast.success(`Successfully updated ${_params.oldValue} to ${_params.newValue}`)
    },
    onError: (error, _params) => {
      toastApiError(error, `Error updating ${_params.oldValue} to ${_params.newValue}`)
    },
  })
}

export const useUpdateAllProductsMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (body: API.UpdateBody) => {
      return api.website.updateSecondaryMarket(body)
    },
    onSuccess: (_data, _params) => {
      toast.success(`Successfully updated ${_params.oldValue} to ${_params.newValue}`)
    },
    onError: (error, _params) => {
      toastApiError(error, `Error updating ${_params.oldValue} to ${_params.newValue}`)
    },
  })
}

export const useAddSecurityToWebsiteMutation = () => {
  const api = useApi()

  const invalidatePosmon = useInvalidatePosmon()

  return useMutation({
    mutationFn: (body: Website.AddInvestmentSecurityToWebsiteBody) => {
      return api.website.addInvestmentSecurityToWebsite(body)
    },
    onSuccess: (_data, _params) => {
      toast.success(`Successfully added ${_params.isin}`)
    },
    onError: (error, _params) => {
      toastApiError(error, `Could not add ${_params.isin} to the website`)
    },
    onSettled: () => {
      invalidatePosmon()
    },
  })
}

export const useRemoveSecurityFromWebsiteMutation = () => {
  const api = useApi()

  const invalidatePosmon = useInvalidatePosmon()

  return useMutation({
    mutationFn: (params: Website.AddInvestmentSecurityToWebsiteBody) => {
      return api.website.removeInvestmentSecurityFromWebsite(params)
    },
    onSuccess: (_data, _params) => {
      toast.success(`Successfully removed ${_params.isin} from website`)
    },
    onError: (error, _params) => {
      toastApiError(error, `Error removing ${_params.isin} from website`)
    },
    onSettled: () => {
      invalidatePosmon()
    },
  })
}

export const useAddScheduleToWebsiteMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (body: Website.AddInvestmentScheduleToWebsiteBody) => {
      return api.website.addInvestmentScheduleToWebsite(body)
    },
    onSuccess: (_data, _params) => {
      toast.success(`Successfully added ${_params.isin}`)
    },
    onError: (error, _params) => {
      toastApiError(error, `Could not add schedule for ${_params.isin} to the website`)
    },
  })
}
